import moment from "moment";
import { useCallback } from "react";

import { BTAvatar, btAvatarColorize, BTText } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { BTLoginTypes } from "@btnet/services-btnet/handlers/models/bTLoginTypes";
import type { UserInteractionsContract } from "@btnet/services-btnet/handlers/models/userInteractionsContract";
import type { UserInteractionsEntityType } from "@btnet/services-btnet/handlers/models/userInteractionsEntityType";
import { UserInteractionsType } from "@btnet/services-btnet/handlers/models/userInteractionsType";
import { cn } from "@btnet/utilities-core";

import {
    BdsIconLove,
    BdsIconLoveFilled,
    BdsIconThumbsUp,
    BdsIconThumbsUpFilled,
} from "@buildertrend/components";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLocalStorage } from "types/btStorage";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";

import "./Reactions.less";

// Helper function to create unique entity key
function getEntityKey(
    entityId: number,
    entityType: UserInteractionsEntityType,
    builderId: number,
): string {
    return `${entityType}-${entityId}-${builderId}`;
}

// Helper functions to manage entity reaction tracking in localStorage
function getEntityReactionMap(): Record<string, boolean> {
    try {
        return BTLocalStorage.get("bt-object-entityReactionMap") || {};
    } catch (error) {
        return {};
    }
}

function setEntityReacted(entityKey: string): void {
    try {
        const currentMap = getEntityReactionMap();
        const updatedMap = {
            ...currentMap,
            [entityKey]: true,
        };
        BTLocalStorage.set("bt-object-entityReactionMap", updatedMap);
    } catch (error) {
        // Silently fail if localStorage is not available
    }
}

function hasEntityBeenReacted(entityKey: string): boolean {
    try {
        const entityMap = getEntityReactionMap();
        return entityMap[entityKey] ?? false;
    } catch (error) {
        return false;
    }
}

interface IReactionsProps {
    entityId: number;
    entityType: UserInteractionsEntityType;
    builderId: number;
    data: UserInteractionsContract[];
    onReact: (data: UserInteractionsContract) => Promise<void>;
    hideCounts?: boolean;
    onlyShowTypes?: UserInteractionsType[];
    buttonType?: "tertiary" | "secondary";
    showNames?: boolean;
    isReadOnly?: boolean;
    className?: string;
    iconOnly?: boolean;
    // we should be able to remove this prop once ClientUpdates is only using mongo
    isLoading?: boolean;
    tooltipOverride?: string;
}

const defaultOnlyShowTypes = Object.values(UserInteractionsType);
export function Reactions(props: IReactionsProps) {
    const userInfo = useRequiredContext(UserInfoContext);
    const userId = userInfo.globalUserId ?? -1;

    const {
        builderId,
        entityId,
        entityType,
        data,
        onReact,
        hideCounts,
        buttonType,
        showNames,
        isReadOnly,
        iconOnly,
        onlyShowTypes = defaultOnlyShowTypes,
        className = "",
        isLoading = false,
        tooltipOverride,
    } = props;

    const handleReact = useCallback(
        async (interactionType: UserInteractionsType) => {
            if (!userInfo.globalUserId) {
                return;
            }
            const entityKey = getEntityKey(entityId, entityType, builderId);

            const hasReactedToThisEntity = hasEntityBeenReacted(entityKey);
            const allowNotifications = !hasReactedToThisEntity;

            const newUserInteraction: UserInteractionsContract = {
                builderId,
                userId: userInfo.globalUserId,
                userName: userInfo.userName,
                entityId,
                entityType,
                interactionType,
                allowNotifications,

                // If user has already reacted, remove the reaction date to unreact
                reactDateUtc: data?.some(
                    (d) =>
                        d.builderId === builderId &&
                        d.userId === userInfo.globalUserId &&
                        d.entityId === entityId &&
                        d.entityType === entityType &&
                        d.interactionType === interactionType &&
                        moment(d.reactDateUtc).isValid(),
                )
                    ? null
                    : new Date(),
                readDateUtc: new Date(),
            };
            setEntityReacted(entityKey);
            await onReact(newUserInteraction);
        },
        [builderId, data, entityId, entityType, onReact, userInfo.globalUserId, userInfo.userName],
    );

    const getTooltip = useCallback(
        (popoverData: UserInteractionsContract[], type: UserInteractionsType) => {
            if (tooltipOverride) {
                return tooltipOverride;
            }

            const tooltipText = isReadOnly ? undefined : getTooltipText(type);
            return showNames && !isReadOnly && popoverData.length > 0 ? (
                <InnerReactionTooltip reactions={popoverData} />
            ) : (
                tooltipText
            );
        },
        [isReadOnly, showNames, tooltipOverride],
    );

    const reactionTypes = [UserInteractionsType.Like, UserInteractionsType.ThumbsUp];

    // If displaying, only show reactions that have a count
    // If not read-only, show all reaction types as toggle for current
    return (
        <div className={cn("ReactionsContainer", className)}>
            {reactionTypes
                .filter((r) => onlyShowTypes.includes(r))
                .map((type) => {
                    const userHasReacted = data.some(
                        (d) =>
                            d.userId === userId &&
                            d.interactionType === type &&
                            moment(d.reactDateUtc).isValid(),
                    );
                    const popoverData = data.filter(
                        (d) => d.interactionType === type && moment(d.reactDateUtc).isValid(),
                    );
                    const tooltipContent = getTooltip(popoverData, type);
                    const reactions = (
                        <div
                            key={type}
                            className={cn("ReactionButton flex align-items-center", {
                                HoverButton: !hideCounts,
                            })}
                            data-testid="ReactionParent"
                        >
                            {!iconOnly ? (
                                <BTPopover
                                    content={tooltipContent}
                                    disabled={!tooltipContent}
                                    overlayStyle={{
                                        width: "max-content",
                                        maxHeight: tooltipOverride ? "48px" : undefined,
                                        maxWidth: tooltipOverride ? "260px" : undefined,
                                    }}
                                >
                                    <BTButton
                                        className="ReactionInnerButton"
                                        type={buttonType ?? "tertiary"}
                                        id={`reactionButton_${type}_${entityId}`}
                                        data-testid={`reactionButton_${type}_${entityId}`}
                                        disabled={isReadOnly}
                                        onClick={isReadOnly ? undefined : () => handleReact(type)}
                                        aria-label="Reactions"
                                        icon={getReactIcon(type, userHasReacted)}
                                        loading={isLoading}
                                    >
                                        {!hideCounts && popoverData.length}
                                    </BTButton>
                                </BTPopover>
                            ) : (
                                <div className="flex flex-row items-center gap-1 grow">
                                    {getReactIcon(type, userHasReacted, className)}

                                    {!hideCounts && (
                                        <BTText
                                            className={className}
                                            text={popoverData.length}
                                            size="distinct-sm"
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    );

                    return reactions;
                })}
        </div>
    );
}

function getReactIcon(type: UserInteractionsType, filled: boolean, className?: string) {
    switch (type) {
        case UserInteractionsType.Like:
            return filled ? (
                <BdsIconLoveFilled className={className} />
            ) : (
                <BdsIconLove className={className} />
            );
        case UserInteractionsType.ThumbsUp:
            return filled ? (
                <BdsIconThumbsUpFilled className={className} />
            ) : (
                <BdsIconThumbsUp className={className} />
            );
    }
}

function getTooltipText(type: UserInteractionsType) {
    switch (type) {
        case UserInteractionsType.Like:
            return "Love";
        case UserInteractionsType.ThumbsUp:
            return "Like";
        default:
            return "";
    }
}

interface IInnerReactionTooltipProps {
    reactions: UserInteractionsContract[];
}

function InnerReactionTooltip({ reactions }: IInnerReactionTooltipProps) {
    const getLabelForType = useCallback((userType: BTLoginTypes) => {
        switch (userType) {
            case BTLoginTypes.BUILDER:
                return "Internal";
            case BTLoginTypes.SUBS:
                return "Subs/vendors";
            case BTLoginTypes.CONTACT:
                return "Client";
        }
        return "Other";
    }, []);

    const getUsersForType = useCallback(
        (t: BTLoginTypes) => {
            const users = reactions.filter((r) => r.loginType === t);
            if (users.length === 0) {
                return null;
            }
            return (
                <div key={t}>
                    <div className="margin-bottom-xss text-small">{getLabelForType(t)}</div>
                    {users.map((u) => (
                        <div className="ReactionPopoverRow" key={u.userId}>
                            <BTAvatar
                                text={u.userName ?? ""}
                                testid={`avatar-${u.userId}`}
                                className="margin-right-xs"
                                colorize={btAvatarColorize[u.userId % btAvatarColorize.length]}
                            />
                            {u.userName}
                        </div>
                    ))}
                </div>
            );
        },
        [reactions, getLabelForType],
    );

    return (
        <div className="UserReactionPopover">
            {getUsersForType(BTLoginTypes.CONTACT)}
            {getUsersForType(BTLoginTypes.BUILDER)}
            {getUsersForType(BTLoginTypes.SUBS)}
        </div>
    );
}
